// ────────────────────────────────────────────────────────────────────────────
//  useExtensions.ts — User's aktive Erweiterungen / Subscriptions
//
//  Holt von /api/v1/products/my — der Worker liefert eigene Käufe + per Org
//  geerbte Slots, deduped nach slug.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { api } from './api'

export interface Extension {
  id:          number
  product:     string                          // 'PunchClock' | 'Recruitment' | …
  grantedVia:  'Purchase' | 'OrgMembership'
  isActive:    boolean
  purchasedAt: string | null
  expiresAt:   string | null
  /// Erweiterung um Plattform — wird vom Worker als 'apple' | 'web' geliefert
  /// (für Kauf via Org → null). Wir leiten das aus apple_transaction_id ab.
  platform?:   'apple' | 'web' | null
}

export interface Product {
  id:                number
  slug:              string
  name:              string
  description:       string
  priceFormatted:    string
  isActive:          boolean
  isSubscription:    boolean
  trialDays:         number
  billingPeriodDays: number | null
  appleProductId:    string | null
  /// Roher Preis in Euro (z.B. 49 oder 3.99) — bei Slot-basiert null
  basePrice:         number | null
}

let cache: Extension[] | null = null
const listeners = new Set<(x: Extension[]) => void>()

export function useExtensions() {
  const [items, setItems] = useState<Extension[]>(cache ?? [])
  const [loading, setLoading] = useState(cache === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fn = (x: Extension[]) => setItems(x)
    listeners.add(fn)
    if (cache === null) {
      api.get<{ items?: Extension[] } | Extension[]>('/products/my')
        .then((res: any) => {
          const arr: Extension[] = Array.isArray(res) ? res : (res.items ?? [])
          cache = arr
          listeners.forEach(f => f(arr))
          setLoading(false)
        })
        .catch(e => {
          setError(e?.message ?? 'load failed')
          cache = []
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
    return () => { listeners.delete(fn) }
  }, [])

  /// Force-refresh
  const refresh = async () => {
    try {
      const res = await api.get<any>('/products/my')
      const arr: Extension[] = Array.isArray(res) ? res : (res.items ?? [])
      cache = arr
      listeners.forEach(f => f(arr))
    } catch (e: any) {
      setError(e?.message ?? 'refresh failed')
    }
  }

  /// Convenience: ist ein bestimmter Slug aktiv?
  const has = (slug: string) => items.some(x => x.product === slug && x.isActive !== false)

  /// True wenn der User irgendein Business-Paket hat (Monthly/Yearly, Basic/L/MAX/…).
  /// Business schaltet automatisch frei: Zeiterfassung, Org-Erstellung, alle
  /// Org-Tools (Mitarbeiter-Slots, Akten, Chat, Stempeluhr für alle).
  const hasBusinessTier = () => items.some(x =>
    x.isActive !== false && /^Business/i.test(x.product)
  )

  /// True wenn der User berechtigt ist eine Organisation zu erstellen.
  /// MUSS exakt synchron mit ORG_CREATION_SLUGS im Backend
  /// (custosoft-workers/src/routes/onboarding.ts) bleiben — sonst zeigt der
  /// Webapp einen Create-Button der vom Server mit 403 abgelehnt wird.
  /// Hinweis: AccountType='Organisation' und appRole≠null zählen ebenfalls,
  /// werden aber NICHT hier geprüft (nur User-Object kennt das) — die
  /// vollständige Eligibility-Prüfung passiert via canCreateOrg(user, exts).
  const hasOrgCreationLicense = () => items.some(x =>
    x.isActive !== false && (ORG_CREATION_SLUGS as readonly string[]).includes(x.product)
  )

  return { items, loading, error, has, hasBusinessTier, hasOrgCreationLicense, refresh }
}

/// Slugs die zur Org-Erstellung berechtigen — synchron mit Backend
/// `ORG_CREATION_SLUGS` in custosoft-workers/src/routes/onboarding.ts.
export const ORG_CREATION_SLUGS = [
  'BusinessBasic', 'BusinessBasicYearly',
  'BusinessL',     'BusinessLYearly',
  'BusinessMAX',   'BusinessMAXYearly',
  'AllInOne',      'AllInOneYearly',
  'Recruitment',   'MoreSpace',
  'Business',                              // Legacy
] as const

/// Vollständige Eligibility-Prüfung (Lizenz ODER Account-Typ ODER Staff-Role).
/// Spiegelt die Backend-Logik in onboarding.ts → POST /onboarding/org.
export function canCreateOrg(opts: {
  accountType?: string | null
  appRole?:     string | null
  extensions?:  Extension[]
}): boolean {
  if (opts.appRole != null) return true
  if (opts.accountType === 'Organisation') return true
  const exts = opts.extensions ?? []
  return exts.some(x => x.isActive !== false && (ORG_CREATION_SLUGS as readonly string[]).includes(x.product))
}

/// Reset Cache (z.B. nach Logout oder Account-Wechsel).
/// MUSS bei jedem Auth-Wechsel aufgerufen werden — sonst sieht der neue User
/// die Extensions des alten Users (weil productsCache modul-weit lebt).
export function resetExtensionsCache() {
  cache = null
  listeners.forEach(f => f([]))
}

// ════════════════════════════════════════════════════════════════════════════
// useProducts — komplette öffentliche Produkt-Liste
// ════════════════════════════════════════════════════════════════════════════
let productsCache: Product[] | null = null
export function resetProductsCache() {
  productsCache = null
}

export function useProducts() {
  const [items, setItems] = useState<Product[]>(productsCache ?? [])
  const [loading, setLoading] = useState(productsCache === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (productsCache !== null) {
      setItems(productsCache); setLoading(false); return
    }
    api.get<{ items?: Product[] } | Product[]>('/products')
      .then((res: any) => {
        const arr: Product[] = Array.isArray(res) ? res : (res.items ?? [])
        productsCache = arr
        setItems(arr)
        setLoading(false)
      })
      .catch(e => {
        setError(e?.message ?? 'load failed')
        productsCache = []
        setLoading(false)
      })
  }, [])

  return { items, loading, error }
}
